import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { cn, sortByKey } from '@repo/ui/utils'

import { Resource } from '@/components/admin/Resource'
import { ResourceCreator, ResourceScanner } from '@/components/admin/ResourceCreator.tsx'
import { buildAnchorIdPrefix } from '@/components/helpers'
import { Matches } from '@/components/search/helpers'
import { HighlightedText } from '@/components/search/HighlightedText'
import { Search } from '@/components/search/Search'
import { Resource as ResourceType } from '@/store/admin'

export type ResourcesProps = { resources: ResourceType[] }

export function Resources({ resources }: ResourcesProps) {
  const { t, tc } = useT({ keyPrefix: 'admin.resources' })
  const sortedResources = [...resources].sort(sortByKey('createdAt', 'desc'))
  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <Header />

      {resources.length === 0 ? (
        <CardContent>{t('noResourceYet')}</CardContent>
      ) : (
        <Search items={sortedResources}>
          {({ matchCount, query, searchResults, currentMatch, matchRefs, expandedItems }) => (
            <CardContent className="pr-0 pt-0 xl:pr-0 xl:pt-0">
              <ScrollArea
                className={cn(
                  'flex flex-col gap-12 pr-4 xl:pr-6',
                  matchCount
                    ? 'max-h-[calc(100vh-17rem)] xl:max-h-[calc(100vh-19rem)]'
                    : 'max-h-[calc(100vh-13.5rem)] xl:max-h-[calc(100vh-15.5rem)]',
                )}
              >
                <div className="flex flex-col gap-6 pt-4 xl:pt-6">
                  {query.length > 1 ? (
                    <>
                      {searchResults.length > 0 ? (
                        searchResults.map(
                          ({
                            item: resource,
                            matches,
                          }: {
                            item: ResourceType
                            matches: Matches<'title' | 'content'>
                          }) => {
                            return (
                              <AugmentedResource
                                key={resource.id + '_' + expandedItems}
                                currentMatch={currentMatch}
                                expanded={expandedItems}
                                findIndex={(anchorPrefix) =>
                                  matchRefs.current.findIndex((id) => id.startsWith(anchorPrefix))
                                }
                                matches={matches}
                                resource={resource}
                              />
                            )
                          },
                        )
                      ) : (
                        <div className="text-center">{tc('noResults', { cfl: true })}</div>
                      )}
                    </>
                  ) : (
                    sortedResources.map((resource: ResourceType) => (
                      <Resource key={resource.id} resource={resource} />
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          )}
        </Search>
      )}
    </Card>
  )
}

function Header() {
  const { t } = useT({ keyPrefix: 'admin.resources' })
  return (
    <CardHeader>
      <CardTitle>{t('title', { cfl: true })}</CardTitle>
      <CardDescription>{t('description')}</CardDescription>

      <CardAction>
        <ResourceCreator />

        <ResourceScanner />
      </CardAction>
    </CardHeader>
  )
}

function AugmentedResource({
  currentMatch,
  expanded,
  findIndex,
  matches,
  resource,
}: {
  currentMatch: number
  expanded: boolean
  findIndex: (id: string) => number
  matches: Matches<'title' | 'content'>
  resource: ResourceType
}) {
  const titleIndices = matches.title
  const contentIndices = matches.content

  const anchorPrefixes = {
    title: buildAnchorIdPrefix(resource.id, 'title'),
    content: buildAnchorIdPrefix(resource.id, 'content'),
  }

  const getCurrentMatchIndex = (type: 'title' | 'content') => {
    const matchIdx = findIndex(anchorPrefixes[type])
    return matchIdx >= 0 ? currentMatch - matchIdx : -1
  }
  const currentMatchIndex = {
    title: getCurrentMatchIndex('title'),
    content: getCurrentMatchIndex('content'),
  }
  return (
    <Resource
      resource={resource}
      controller={{
        defaultOpen: expanded,
      }}
      contentComp={
        <HighlightedText
          text={resource['content']}
          indices={contentIndices}
          currentMatchIndex={currentMatchIndex.content}
          anchorPrefix={anchorPrefixes.content}
        />
      }
      titleComp={
        <HighlightedText
          text={resource['title']}
          indices={titleIndices}
          currentMatchIndex={currentMatchIndex.title}
          anchorPrefix={anchorPrefixes.title}
        />
      }
    />
  )
}
